import React, { useEffect, useState, useCallback } from 'react';
import { Table, Pagination, PaginationItem, PaginationLink, Input } from 'reactstrap';
import { NavLink } from 'react-router-dom';
import moment from 'moment';

import { AltCheckbox } from '../../components/AltCheckbox';
import { getVouchers, putVoucher } from '../../actions/ActionVoucher';
import { can } from '../../utils/permissions';

const VoucherIndex = ({ setAlert }) => {
  const [aVouchers, setVouchers] = useState([]);
  const [bLoading, setLoading] = useState(false);

  // estado de paginação
  const [nPage, setPage] = useState(1);
  const [nPerPage, setPerPage] = useState(10);
  const [nTotal, setTotal] = useState(0);
  const [nLastPage, setLastPage] = useState(1);

  const loadVouchers = useCallback(async () => {
    try {
      setLoading(true);
      const oPaginator = await getVouchers(nPerPage, nPage);
      const aData = (oPaginator && oPaginator.data) ? oPaginator.data : [];
      setVouchers(aData);
      setPage(oPaginator && oPaginator.current_page ? oPaginator.current_page : 1);
      setPerPage(oPaginator && oPaginator.per_page ? Number(oPaginator.per_page) : 10);
      setTotal(oPaginator && typeof oPaginator.total !== 'undefined' ? Number(oPaginator.total) : 0);
      setLastPage(oPaginator && oPaginator.last_page ? Number(oPaginator.last_page) : 1);
    } catch (oError) {
      setAlert('error', oError.aMsgErrors);
    } finally {
      setLoading(false);
    }
  }, [nPerPage, nPage, setAlert]);

  useEffect(() => {
    loadVouchers();
  }, [loadVouchers]);

  const handleChange = async (evt, index) => {
    try {
      const { type, name, checked, value } = evt.target;

      const aNext = [...aVouchers];
      const oVoucher = { ...aNext[index] };

      if (type === 'checkbox') {
        oVoucher[name] = checked;
      } else if (value !== null) {
        oVoucher[name] = value;
      }

      setLoading(true);
      await putVoucher(oVoucher);
      aNext[index] = oVoucher;
      setVouchers(aNext);
    } catch (oError) {
      setAlert('error', oError.aMsgErrors);
    } finally {
      setLoading(false);
    }
  };

  // navegação de páginas
  const goToPage = (p) => {
    if (p < 1 || p > nLastPage || p === nPage) return;
    setPage(p);
  };

  // range de páginas (janela de até 5 páginas)
  const getPageNumbers = () => {
    const windowSize = 5;
    let start = Math.max(1, nPage - Math.floor(windowSize / 2));
    let end = start + windowSize - 1;
    if (end > nLastPage) {
      end = nLastPage;
      start = Math.max(1, end - windowSize + 1);
    }
    const nums = [];
    for (let i = start; i <= end; i++) nums.push(i);
    return nums;
  };

  const onChangePerPage = (evt) => {
    const newPer = Number(evt.target.value) || 10;
    setPerPage(newPer);
    setPage(1); // resetar para primeira página ao mudar o per_page
  };

  return (
    <>
      <div className="title-button">
        <h1>Vouchers Disponíveis</h1>

        <div className="button-stack">
          {can('edit-plan') && (
              <NavLink className="btn alternate" to="/Voucher/Create" title="Voucher">
                Criar voucher
              </NavLink>
          )}
        </div>
      </div>

      {can('view-plan') && (
        <>
          {/* Barra de controle: Itens por página + contagem */}
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="d-flex align-items-center gap-2">
              <span>Itens por página:</span>
              <Input
                type="select"
                value={nPerPage}
                onChange={onChangePerPage}
                style={{ width: 90 }}
                disabled={bLoading}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Input>
            </div>
            <div>
              {nTotal} resultado{nTotal === 1 ? '' : 's'} • Página {nPage} de {nLastPage}
            </div>
          </div>

          <Table hover responsive striped>
            <thead>
              <tr>
                <th>#</th>
                <th>Código</th>
                <th>Desconto(%)</th>
                <th>Responsável</th>
                <th>Início/Dias</th>
                <th>Descrição</th>
                <th>Ativo</th>
                <th>Data de Expiração</th>
                <th>Data de Criação</th>
              </tr>
            </thead>
            <tbody>
              {aVouchers.map((oVoucher, i) => (
                <tr key={oVoucher.vch_nid}>
                  <td>{oVoucher.vch_nid}</td>
                  <td title={oVoucher.vch_ccode}>
                    <span>{oVoucher.vch_ccode}</span>
                  </td>
                  <td>{String(oVoucher.vch_ndiscount) + '%'}</td>
                  <td>{oVoucher.user ? oVoucher.user.adm_cname : ''}</td>
                  <td>{oVoucher.vch_ndaysbegin}</td>
                  <td>
                    <span className="abbreviate-text">{oVoucher.vch_cdescription}</span>
                  </td>
                  <td className="text-center">
                    <AltCheckbox
                      name="vch_bactive"
                      id={'vch_bactive_' + oVoucher.vch_nid}
                      onChange={(evt) => handleChange(evt, i)}
                      checked={!!oVoucher.vch_bactive}
                      disabled={!can('edit-plan') || bLoading}
                    />
                  </td>
                  <td>
                    {oVoucher.vch_dexpiration
                      ? moment.parseZone(oVoucher.vch_dexpiration).format('DD/MM/YYYY')
                      : ''}
                  </td>
                  <td>{moment.parseZone(oVoucher.vch_dcreated).format('DD/MM/YYYY H:mm')}</td>
                </tr>
              ))}

              {!bLoading && aVouchers.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center">Nenhum voucher encontrado.</td>
                </tr>
              )}
              {bLoading && (
                <tr>
                  <td colSpan={9} className="text-center">Carregando...</td>
                </tr>
              )}
            </tbody>
          </Table>

          {/* Paginação */}
          {nLastPage > 1 && (
            <div className="d-flex justify-content-center">
              <Pagination>
                <PaginationItem disabled={nPage === 1 || bLoading}>
                  <PaginationLink first onClick={() => goToPage(1)} />
                </PaginationItem>
                <PaginationItem disabled={nPage === 1 || bLoading}>
                  <PaginationLink previous onClick={() => goToPage(nPage - 1)} />
                </PaginationItem>

                {getPageNumbers().map((num) => (
                  <PaginationItem key={num} active={num === nPage} disabled={bLoading}>
                    <PaginationLink onClick={() => goToPage(num)}>{num}</PaginationLink>
                  </PaginationItem>
                ))}

                <PaginationItem disabled={nPage === nLastPage || bLoading}>
                  <PaginationLink next onClick={() => goToPage(nPage + 1)} />
                </PaginationItem>
                <PaginationItem disabled={nPage === nLastPage || bLoading}>
                  <PaginationLink last onClick={() => goToPage(nLastPage)} />
                </PaginationItem>
              </Pagination>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default VoucherIndex;
